import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { useSchoolRealtime } from '../hooks/useSchoolRealtime';
import { supabase } from '../lib/supabase';

export type RealtimeUpdate = {
  type: string;
  payload: unknown;
};

export type GroupedRealtimeUpdates = Record<string, RealtimeUpdate[]>;

interface SchoolRealtimeStatus {
  isConnected: boolean;
  channelCount: number;
}

interface SchoolRealtimeContextValue {
  subscribe: (type: string, handler: (updates: RealtimeUpdate[]) => void) => () => void;
  status: SchoolRealtimeStatus;
  setTeacherLessonIds: (ids: string[]) => void;
}

const SchoolRealtimeContext = createContext<SchoolRealtimeContextValue | undefined>(undefined);

export function SchoolRealtimeProvider({ children }: { children: React.ReactNode }) {
  const { user, schoolId } = useAuth() as any;
  const [status, setStatus] = useState<SchoolRealtimeStatus>({ isConnected: false, channelCount: 0 });
  const [teacherLessonIds, setTeacherLessonIdsState] = useState<string[]>([]);
  const listenersRef = useRef<Map<string, Set<(updates: RealtimeUpdate[]) => void>>>(new Map());
  const fallbackChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const subscribe = useCallback(
    (type: string, handler: (updates: RealtimeUpdate[]) => void) => {
      let listeners = listenersRef.current.get(type);
      if (!listeners) {
        listeners = new Set();
        listenersRef.current.set(type, listeners);
      }
      listeners.add(handler);

      return () => {
        const current = listenersRef.current.get(type);
        if (!current) return;
        current.delete(handler);
        if (current.size === 0) {
          listenersRef.current.delete(type);
        }
      };
    },
    [],
  );

  const setTeacherLessonIds = useCallback((ids: string[]) => {
    setTeacherLessonIdsState(ids);
  }, []);

  const handleRealtimeUpdate = useCallback((groupedUpdates: GroupedRealtimeUpdates) => {
    console.log('[RT-Context] Received updates:', Object.keys(groupedUpdates));

    for (const [type, updates] of Object.entries(groupedUpdates)) {
      const listeners = listenersRef.current.get(type);
      if (!listeners || updates.length === 0) continue;

      listeners.forEach((listener) => {
        try {
          listener(updates);
        } catch (error) {
          try {
            // eslint-disable-next-line no-console
            console.error('SchoolRealtimeProvider listener error', error);
          } catch { }
        }
      });
    }

    const allListeners = listenersRef.current.get('all');
    if (allListeners) {
      const flattened = Object.values(groupedUpdates).flat();
      if (flattened.length > 0) {
        allListeners.forEach((listener) => {
          try {
            listener(flattened);
          } catch (error) {
            try {
              // eslint-disable-next-line no-console
              console.error('SchoolRealtimeProvider listener error', error);
            } catch { }
          }
        });
      }
    }
  }, []);

  const enabled = !!user && !!schoolId;

  const realtime: any = useSchoolRealtime({
    schoolId,
    teacherLessonIds,
    onUpdate: enabled ? handleRealtimeUpdate : undefined,
    enabled,
  });

  useEffect(() => {
    setStatus({
      isConnected: !!realtime?.isConnected,
      channelCount: Number(realtime?.channelCount || 0),
    });
  }, [realtime?.isConnected, realtime?.channelCount]);

  useEffect(() => {
    if (!user || !schoolId) {
      if (fallbackChannelRef.current) {
        try {
          supabase.removeChannel(fallbackChannelRef.current);
        } catch { }
        fallbackChannelRef.current = null;
      }
      return;
    }

    if (realtime?.channelCount && realtime.channelCount > 0) {
      if (fallbackChannelRef.current) {
        try {
          supabase.removeChannel(fallbackChannelRef.current);
        } catch { }
        fallbackChannelRef.current = null;
      }
      return;
    }

    // DISABLE FALLBACK to prevent conflicts
    return;
  }, [user, schoolId, realtime?.channelCount, handleRealtimeUpdate]);

  // DIRECT DEBUG SUBSCRIPTION REMOVED

  const value = useMemo<SchoolRealtimeContextValue>(
    () => ({
      subscribe,
      status,
      setTeacherLessonIds,
    }),
    [subscribe, status, setTeacherLessonIds],
  );

  return <SchoolRealtimeContext.Provider value={value}>{children}</SchoolRealtimeContext.Provider>;
}

export function useSchoolRealtimeContext(): SchoolRealtimeContextValue {
  const context = useContext(SchoolRealtimeContext);
  if (!context) {
    throw new Error('useSchoolRealtimeContext must be used within a SchoolRealtimeProvider');
  }
  return context;
}
